import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { Button, Modal } from "north.js";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Header, PageWrapper } from "src/shared/components";
import { errorToast } from "src/shared/components/Toast";
import { useDeviceSize } from "src/shared/hooks";
import { formatValidationIssues } from "src/shared/utils/validationMessages";
import { getPublishValidationIssues } from "src/views/profile/components/ProfileForm/constants";
import ContactFields from "src/views/profile/components/ProfileForm/sections/ContactFields";
import CoreFields from "src/views/profile/components/ProfileForm/sections/CoreFields";
import TagsFields from "src/views/profile/components/ProfileForm/sections/TagsFields";
import TeamFields from "src/views/profile/components/ProfileForm/sections/TeamFields";
import {
  ProfileFormValues,
  TargetStage,
} from "src/views/profile/components/ProfileForm/types";

import DraftProfileBanner from "../DraftProfileBanner";

const DRAFT_WARNING =
  "Save as Draft will hide your profile from the hacker directory until you publish it again.";

const EditProfile: React.FC<{
  values: ProfileFormValues;
  isPublished: boolean;
  isSubmitting: boolean;
  onSubmit: (
    values: ProfileFormValues,
    targetStage: TargetStage
  ) => Promise<void>;
  onCancel: () => void;
}> = ({ values, isPublished, isSubmitting, onSubmit, onCancel }) => {
  const isMediumOrSmaller = useDeviceSize("medium");
  const [staging, setStaging] = useState<ProfileFormValues>(values);
  const [showErrors, setShowErrors] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  useEffect(() => {
    setStaging(values);
  }, [values]);

  const setStagingField = <K extends keyof ProfileFormValues>(
    field: K,
    value: ProfileFormValues[K]
  ) => {
    setStaging((previous) => ({ ...previous, [field]: value }));
  };

  const handleSubmit = async (targetStage: TargetStage) => {
    const validationIssues = getPublishValidationIssues(staging);
    const canPublish = validationIssues.every(({ isValid }) => isValid);
    if (targetStage === "published" && !canPublish) {
      setShowErrors(true);
      window.scrollTo(0, 0);
      errorToast(formatValidationIssues(validationIssues));
      return;
    }

    await onSubmit(staging, targetStage);
    setShowErrors(false);
  };

  const actions = (
    <Actions
      isPublished={isPublished}
      isSubmitting={isSubmitting}
      isMobile={isMediumOrSmaller}
      onSubmit={handleSubmit}
    />
  );

  return (
    <EditShell>
      <Header
        title="My Profile"
        subtitle="Edit your directory card details, update your project goals, and manage your team status."
      />
      <Modal
        open={showDiscardModal}
        onClose={() => setShowDiscardModal(false)}
        style={{ width: "min(420px, calc(100vw - 32px))" }}
      >
        <Modal.Header>Discard changes?</Modal.Header>
        <Modal.Body>
          You have unsaved edits. Discard them and leave the profile editor?
        </Modal.Body>
        <Modal.Footer justify="end">
          <Button
            size="md"
            color="primary"
            onClick={() => setShowDiscardModal(false)}
          >
            Keep editing
          </Button>
          <Button
            size="md"
            color="secondary"
            onClick={() => {
              setShowDiscardModal(false);
              onCancel();
            }}
          >
            Discard changes
          </Button>
        </Modal.Footer>
      </Modal>
      <EditPageWrapper stretchHeight={false}>
        <CancelLink
          type="button"
          disabled={isSubmitting}
          onClick={() => {
            if (JSON.stringify(staging) === JSON.stringify(values)) {
              onCancel();
              return;
            }
            setShowDiscardModal(true);
          }}
        >
          <ArrowLeftIcon aria-hidden="true" />
          Discard changes
        </CancelLink>
        <EditLayout>
          <FormColumn>
            {!isPublished && <DraftProfileBanner />}
            <EditFields>
              <EditSection>
                <CoreFields
                  staging={staging}
                  setStagingField={setStagingField}
                  showErrors={showErrors}
                />
              </EditSection>
              <EditSection>
                <TagsFields
                  staging={staging}
                  setStagingField={setStagingField}
                  showErrors={showErrors}
                />
              </EditSection>
              <EditSectionDivider />
              <EditSection>
                <TeamFields
                  staging={staging}
                  setStagingField={setStagingField}
                  showErrors={showErrors}
                  showInfoBanner={false}
                  mode="standalone"
                />
              </EditSection>
              <EditSectionDivider />
              <EditSection>
                <ContactFields
                  staging={staging}
                  setStagingField={setStagingField}
                  showErrors={showErrors}
                />
              </EditSection>
            </EditFields>
          </FormColumn>
          <PublishingActions>{actions}</PublishingActions>
        </EditLayout>
      </EditPageWrapper>
    </EditShell>
  );
};

const Actions: React.FC<{
  isPublished: boolean;
  isSubmitting: boolean;
  isMobile: boolean;
  onSubmit: (targetStage: TargetStage) => Promise<void>;
}> = ({ isPublished, isSubmitting, isMobile, onSubmit }) => {
  const tooltipId = "save-profile-as-draft-tooltip";
  const mobileWarningId = "save-profile-as-draft-mobile-warning";

  return (
    <ActionsGroup>
      <ActionsRow>
        <PublishButton
          color="primary"
          size="lg"
          disabled={isSubmitting}
          onClick={() => void onSubmit("published")}
        >
          Publish Profile
        </PublishButton>
        <DraftTooltipTarget>
          <DraftButton
            color="secondary"
            size="lg"
            disabled={isSubmitting}
            aria-describedby={
              isPublished ? (isMobile ? mobileWarningId : tooltipId) : undefined
            }
            onClick={() => void onSubmit("draft")}
          >
            Save as Draft
          </DraftButton>
          {isPublished && !isMobile && (
            <DraftTooltip id={tooltipId} role="tooltip">
              {DRAFT_WARNING}
            </DraftTooltip>
          )}
        </DraftTooltipTarget>
      </ActionsRow>
      {isPublished && isMobile && (
        <MobileDraftWarning id={mobileWarningId}>
          {DRAFT_WARNING}
        </MobileDraftWarning>
      )}
    </ActionsGroup>
  );
};

export default EditProfile;

/** Mobile: fill the viewport; desktop: pass through so layout is unchanged. */
const EditShell = styled.div`
  display: contents;

  ${({ theme }) => theme.mediaQueries.medium`
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
  `}
`;

const CancelLink = styled.button`
  display: inline-flex;
  margin-bottom: 40px;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  cursor: pointer;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 800;
  line-height: 1.4;
  text-decoration: underline;

  &:hover,
  &:focus {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.globalConstants.color.borderFocus};
    outline-offset: 2px;
    border-radius: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  ${({ theme }) => theme.mediaQueries.medium`
    flex: 0 0 auto;
    margin-bottom: 16px;
  `}
`;

const EditPageWrapper = styled(PageWrapper)`
  padding-bottom: 64px;

  ${({ theme }) => theme.mediaQueries.medium`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));

    /* PageWrapper wraps children in a SubContainer — stretch it too. */
    & > * {
      display: flex;
      width: 100%;
      max-width: 100%;
      flex: 1 1 auto;
      flex-direction: column;
      min-height: 0;
    }
  `}
`;

const EditLayout = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  gap: 48px;

  ${({ theme }) => theme.mediaQueries.medium`
    flex: 1 1 auto;
    flex-direction: column;
    align-items: stretch;
    min-height: 0;
    overflow: hidden;
    gap: 16px;
  `}
`;

const FormColumn = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  max-width: 700px;
  flex: 0 1 700px;
  flex-direction: column;
  gap: 32px;

  ${({ theme }) => theme.mediaQueries.medium`
    flex: 1 1 auto;
    flex-basis: auto;
    max-width: none;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  `}
`;

const EditFields = styled.div`
  width: 100%;
`;

const EditSection = styled.section`
  margin-bottom: 32px;
`;

const EditSectionDivider = styled.hr`
  margin: 8px 0 32px;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
`;

const PublishingActions = styled.aside`
  position: sticky;
  z-index: 20;
  top: 32px;
  flex: 0 0 auto;
  margin-left: auto;

  ${({ theme }) => theme.mediaQueries.medium`
    position: static;
    z-index: auto;
    top: auto;
    flex: 0 0 auto;
    width: 100%;
    margin-left: 0;
  `}
`;

const ActionsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${({ theme }) => theme.mediaQueries.medium`
    width: 100%;
    max-width: 700px;
    margin: 0 auto;
  `}
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  ${({ theme }) => theme.mediaQueries.medium`
    flex-wrap: nowrap;
  `}
`;

const PublishButton = styled(Button)`
  white-space: nowrap;

  ${({ theme }) => theme.mediaQueries.medium`
    width: 100%;
    flex: 1 1 0;
  `}
`;

const DraftButton = styled(Button)`
  white-space: nowrap;

  ${({ theme }) => theme.mediaQueries.medium`
    width: 100%;
  `}
`;

const DraftTooltipTarget = styled.span`
  position: relative;
  display: inline-flex;

  &:hover [role="tooltip"],
  &:focus-within [role="tooltip"] {
    opacity: 1;
    visibility: visible;
  }

  ${({ theme }) => theme.mediaQueries.medium`
    flex: 1 1 0;
  `}
`;

const DraftTooltip = styled.span`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  z-index: 1001;
  box-sizing: border-box;
  width: 210px;
  max-width: calc(100vw - 48px);
  padding: 8px 12px;
  transform: translateX(-50%);
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 1px 2px rgb(0 0 0 / 6%), 0 1px 3px rgb(0 0 0 / 10%);
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
  opacity: 0;
  visibility: hidden;
  transition: opacity 120ms ease, visibility 120ms ease;
  pointer-events: none;

  &::before,
  &::after {
    position: absolute;
    left: 50%;
    width: 0;
    height: 0;
    transform: translateX(-50%);
    border-right: 7px solid transparent;
    border-left: 7px solid transparent;
    content: "";
  }

  &::before {
    top: -8px;
    border-bottom: 8px solid
      ${({ theme }) => theme.globalConstants.color.borderGray};
  }

  &::after {
    top: -7px;
    border-bottom: 8px solid ${({ theme }) => theme.globalConstants.color.white};
  }

  ${({ theme }) => theme.mediaQueries.medium`
    display: none;
  `}
`;

const MobileDraftWarning = styled.p`
  display: none;

  ${({ theme }) => theme.mediaQueries.medium`
    display: block;
    width: 100%;
    margin: 0;
    color: ${theme.globalConstants.color.textTertiary};
    font-family: ${theme.globalConstants.fontFamily.body};
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
  `}
`;
